import { Link, useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/*
 * The frame every Meta page shares: one heading, one line on what this section
 * is, and the tabs. The sidebar group «Meta» points at the same four routes,
 * so the tabs are the in-page way to move between them.
 */

export const META_TABS = [
  { path: '/meta', label: 'Kontoen', hint: 'Det som faktisk er bygget i Meta, live' },
  { path: '/meta/kunder', label: 'Kunder', hint: 'Hver person fra lead-skjemaene: annonse, booking, oppmøte, medlem' },
  { path: '/meta/produksjon', label: 'Produksjon', hint: 'Vinkler × avsendere: hva er laget, testet og vunnet' },
  { path: '/meta/system', label: 'Systemet', hint: 'Hvordan det bør se ut, og hvorfor' },
  { path: '/meta/dorer', label: 'Inngangsdører', hint: 'Hvem vi snakker til, og hva som er testet' },
  { path: '/meta/historikk', label: 'Historikken', hint: 'Hva som skjedde fra mai til i dag' },
] as const;

export function MetaShell({
  title,
  lead,
  aside,
  children,
}: {
  title: string;
  lead: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      <nav aria-label="Meta" className="-mx-1 flex gap-1 overflow-x-auto px-1">
        {META_TABS.map((t) => {
          const on = pathname === t.path;
          return (
            <Link
              key={t.path}
              to={t.path}
              title={t.hint}
              className={cn(
                'flex h-10 shrink-0 items-center rounded-full border px-4 text-label transition-fast sm:h-8',
                on
                  ? 'border-fg/40 bg-surface-3 font-medium text-fg'
                  : 'border-border bg-surface text-fg-muted hover:bg-surface-2 hover:text-fg',
              )}
            >
              {t.label}
            </Link>
          );
        })}
      </nav>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-title text-fg">{title}</h1>
          <div className="max-w-3xl text-body text-fg-muted">{lead}</div>
        </div>
        {aside}
      </div>
      {children}
    </div>
  );
}

/** Segmented two-way toggle used on the map. */
export function Toggle<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex rounded-full border border-border bg-surface p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'h-9 whitespace-nowrap rounded-full px-3 text-label transition-fast sm:h-7',
            value === o.value ? 'bg-surface-3 font-medium text-fg' : 'text-fg-muted hover:text-fg',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
